/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useRef } from 'preact/hooks';
import { LoadMoreCollection } from '../PRIVATE_LoadMoreCollection';
import { TableColumn } from './Table';
import { TableSkeletonRow } from './TableSkeletonRow';
import { MemoizeTableRow } from './TableRow';
import { ItemContext } from '../UNSAFE_Collection';
import {
  Cell,
  CurrentRowVariant,
  TableSelectionDetail,
  TableRowContext
} from '../UNSAFE_TableView';
import { classNames } from '../utils/UNSAFE_classNames';
import { collectionStyles } from './themes/Table.css';
import { Keys, containsKey } from '../utils/UNSAFE_keys';
import { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import { TableNoData } from './TableNoData';
import { ComponentChildren } from 'preact';

/**
 * Props for the TableBody Component
 */
export type TableBodyProps<K, D, C> = {
  tableId: string;
  getAccessibleRowHeaders: (context: TableRowContext<K, D>) => Set<C>;
  data: D[] | null;
  columnsArray: TableColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  isShowFocusRing: boolean;
  getRowKey: (data: D) => K;
  hasMore: boolean;
  isRtl: boolean;
  onLoadMore: () => void;
  viewportConfig: ViewportConfig;
  currentCell?: Cell<K, C>;
  currentRowVariant?: CurrentRowVariant;
  selected: { row?: Keys<K>; column?: Keys<C> };
  isRowSelectionEnabled: boolean;
  isSingleRowSelectionEnabled: boolean;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  noDataRenderer?: () => ComponentChildren;
  startStickyEdge?: number;
  endStickyEdge?: number;
};

/**
 * The internal component used to render a single cell in a TableRow.
 */
export function TableBody<K, D, C>({
  tableId,
  getAccessibleRowHeaders,
  data,
  columnsArray,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  isShowFocusRing,
  getRowKey,
  hasMore,
  onLoadMore,
  viewportConfig,
  currentCell,
  currentRowVariant,
  selected,
  isRowSelectionEnabled,
  isSingleRowSelectionEnabled,
  onSelectionChange,
  noDataRenderer,
  startStickyEdge,
  endStickyEdge,
  isRtl
}: TableBodyProps<K, D, C>) {
  // track the empty loading state and generate a new key when it changes to avoid
  // issues with skeleton animations becoming out of sync when more are added
  const loadIndicatorKeyRef = useRef<number>(new Date().getTime());
  const isShowEmptyLoadingRef = useRef<boolean>();

  const collectionClasses = classNames([collectionStyles.base]);

  // returns a TableRowContext based on ItemContext (added key)
  const getRowContext = useCallback(
    (context: ItemContext<{ data: D }>) => {
      return {
        index: context.index,
        data: context.data.data,
        key: getRowKey(context.data.data)
      };
    },
    [getRowKey]
  );

  let isPreviousSelected = false;
  const collectionChildrenFunc = (context: ItemContext<{ data: D }>) => {
    const tableRowContext = getRowContext(context);
    const rowKey = tableRowContext.key;
    const containsCurrentCell = currentCell?.rowKey === rowKey;
    const focusRingColumnKey =
      containsCurrentCell && isShowFocusRing ? currentCell.columnKey : undefined;
    const isSelected = containsKey(selected.row, rowKey);
    const rowProps = {
      tableId: tableId,
      columnsArray: columnsArray,
      rowHeaderColumnKeys: getAccessibleRowHeaders(tableRowContext),
      hasVerticalGridlines: hasVerticalGridlines,
      hasHorizontalGridlines: hasHorizontalGridlines,
      isSelected: isSelected,
      isPreviousSelected: isPreviousSelected,
      onSelectionChange: onSelectionChange,
      isRowSelectionEnabled: isRowSelectionEnabled,
      isSingleRowSelectionEnabled: isSingleRowSelectionEnabled,
      variant: containsCurrentCell ? currentRowVariant : 'none',
      focusRingColumnKey: focusRingColumnKey,
      selectedRowKeys: selected.row,
      selectedColumnKeys: selected.column,
      rowIndex: tableRowContext.index,
      rowKey: rowKey,
      rowData: tableRowContext.data,
      isFinalRow: tableRowContext.index === (data != null ? data.length - 1 : -1),
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      isRtl: isRtl
    };
    isPreviousSelected = isSelected;
    return <MemoizeTableRow {...rowProps} />;
  };

  const getNoDataProps = () => {
    return {
      tableId: tableId,
      columnsCount: columnsArray.length,
      isShowFocusRing: isShowFocusRing && currentCell?.type === 'noData',
      renderer: noDataRenderer
    };
  };

  const collectionData =
    data != null
      ? data.map((d: D) => {
          const key = getRowKey(d);
          return { data: d, metadata: key };
        })
      : [];

  const isShowEmptyLoading = data != null && data.length === 0 && hasMore;
  const isShowNoData = data == null || (data.length === 0 && !hasMore);
  const prevIsEmpty = isShowEmptyLoadingRef.current;
  if (prevIsEmpty !== isShowEmptyLoading) {
    loadIndicatorKeyRef.current = new Date().getTime();
  }
  isShowEmptyLoadingRef.current = isShowEmptyLoading;

  return (
    <tbody class={collectionClasses}>
      {isShowNoData ? (
        <TableNoData {...getNoDataProps()} />
      ) : (
        <LoadMoreCollection
          data={collectionData}
          loadMoreIndicator={
            <TableSkeletonRow
              colspan={columnsArray.length}
              preactKey={loadIndicatorKeyRef.current}
              minimumCount={isShowEmptyLoading ? 25 : 3}
            />
          }
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          viewportConfig={viewportConfig}>
          {collectionChildrenFunc}
        </LoadMoreCollection>
      )}
    </tbody>
  );
}
